import { useEffect } from 'react'

import { UploadType, useUploader } from '@buffer-mono/uploader'
import type { TusTransport } from '@buffer-mono/uploader'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { env } from '~publish/env'
import { prepareUploadFile } from '~publish/helpers/prepareUploadFile'
import { useAppDispatch } from '~publish/legacy/store/index'

import { bindUploaderToReduxSlice } from '../state/bindUploaderToReduxSlice'
import TusUploadsUppyPlugin from '../uppy/TusUploadsUppyPlugin'

// The uploader rebuilds its Uppy instance whenever the transport changes
// identity, so it is shared rather than built per render.
const TUS: TusTransport = { type: 'tus', endpoint: env.VITE_UPLOADS_API }

export default function usePublishUploader(
  props: Omit<
    Parameters<typeof useUploader>[0],
    'organizationId' | 'userId' | 'transport'
  >,
): ReturnType<typeof useUploader> {
  const dispatch = useAppDispatch()
  const accountId = useAccountId()
  // Using Context instead of Redux for organizationId since Context data
  // is available earlier, preventing tracking violations during extension
  // idea creation. Empty string default is safe as no uploads occur earlier
  // than the extension flow.
  const organizationId = useOrganizationId() || ''

  const uploader = useUploader({
    organizationId,
    userId: accountId ?? '',
    ...props,
    id: props.id || UploadType.postAsset,
    transport: TUS,
    prepareFile: prepareUploadFile,
  })

  useEffect(() => {
    bindUploaderToReduxSlice(uploader, { dispatch })
  }, [uploader, dispatch])

  useEffect(() => {
    uploader.uppyInstance.use(TusUploadsUppyPlugin, {
      onComplete: (files) => {
        uploader.emit('upload-finished', files)
      },
      onError: (file, error) => {
        uploader.emit('upload-failed', file, error)
      },
    })
  }, [uploader])

  return uploader
}
